import React, { ReactNode, createContext, useContext, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Workout } from 'src/dao/core-dao'
import { cn } from 'src/lib/utils'

interface WorkoutCardContextValue {
  workout: Workout
  isCompleted?: boolean
  onAction?: () => void
}

const WorkoutCardContext = createContext<WorkoutCardContextValue | undefined>(undefined)

export const useWorkoutCardContext = (): WorkoutCardContextValue => {
  const context = useContext(WorkoutCardContext)
  if (!context) {
    throw new Error('WorkoutCard components must be used within WorkoutCard')
  }
  return context
}

interface WorkoutCardProps {
  workout: Workout
  isCompleted?: boolean
  className?: string
  children: ReactNode
  onAction: () => void
}

export const WorkoutCard = ({
  workout,
  isCompleted = false,
  className,
  children,
  onAction,
}: WorkoutCardProps): React.ReactElement => {
  const contextValue = useMemo(
    () => ({
      workout,
      isCompleted,
      onAction,
    }),
    [workout, isCompleted, onAction],
  )

  const cardContent = (
    <WorkoutCardContext.Provider value={contextValue}>
      <div data-testid="workout-card" className={cn('relative overflow-hidden cursor-pointer aspect-[16/9]', className)}>
        {children}
      </div>
    </WorkoutCardContext.Provider>
  )

  if (onAction) {
    return (
      <div onClick={onAction} className="cursor-pointer h-full">
        {cardContent}
      </div>
    )
  }

  return (
    <Link to={`/workouts/${workout.id}`} className="block no-underline h-full">
      {cardContent}
    </Link>
  )
}
